import { DEBOUNCE_TIME } from '@issuemanager/apps/client/utils';
import { AutocompleteInputChangeReason, debounce } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  useInfiniteIssueSubtypes,
  UseInfiniteIssueSubtypesParamsObject,
  useInfiniteIssueTypes,
  UseIssueTypesParamsObject,
} from '../../features';

type UseIssueTypeIssueSubtypeInfiniteScrollParams = {
  selectedIssueType: string | null;
  issueTypeQueryParams?: UseIssueTypesParamsObject & {
    handleError?: boolean;
  };
  issueSubtypeQueryParams?: UseInfiniteIssueSubtypesParamsObject & {
    handleError?: boolean;
  };
  enabled?: boolean;
};

export function useIssueTypeIssueSubtypeInfiniteScroll(
  params: UseIssueTypeIssueSubtypeInfiniteScrollParams,
) {
  const {
    selectedIssueType,
    issueTypeQueryParams = {},
    issueSubtypeQueryParams = {},
    enabled = true,
  } = params;
  const [issueTypeFilterByText, setIssueTypeFilterByText] =
    useState<string>('');
  const [subtypeFilterByText, setSubtypeFilterByText] = useState<string>('');

  const issueTypesQuery = useInfiniteIssueTypes({
    params: {
      filterByEnabled: true,
      sortByName: 'asc',
      ...issueTypeQueryParams,
      filterByName: issueTypeQueryParams.filterByName || issueTypeFilterByText,
    },
    enabled,
    handleError: issueTypeQueryParams.handleError,
  });
  const issueTypeOptions =
    issueTypesQuery.data?.pages.map((c) => c.data).flat() ?? [];
  const selectedIssueTypeData = issueTypeOptions.find(
    (issueType) => issueType.id === selectedIssueType,
  );

  const issueSubtypesQuery = useInfiniteIssueSubtypes({
    params: {
      filterByName: subtypeFilterByText,
      filterByEnabled: true,
      filterByIssueTypes: selectedIssueTypeData
        ? [selectedIssueTypeData?.name]
        : [],
      ...issueSubtypeQueryParams,
    },
    enabled: enabled && !!selectedIssueType,
    handleError: issueSubtypeQueryParams.handleError,
  });
  const subtypesOptions =
    issueSubtypesQuery.data?.pages.map((c) => c.data).flat() ?? [];

  const handleInputChange = useCallback(
    debounce(
      (
        value: string,
        field: 'issueType' | 'issueSubtype',
        reason: AutocompleteInputChangeReason,
      ) => {
        if (reason === 'reset') return;
        if (field === 'issueType') {
          setIssueTypeFilterByText(value);
        } else {
          setSubtypeFilterByText(value);
        }
      },
      DEBOUNCE_TIME,
    ),
    [],
  );

  useEffect(() => {
    if (selectedIssueType) {
      setSubtypeFilterByText('');
    }
  }, [selectedIssueType]);

  return {
    handleInputChange,
    issueTypeOptions,
    subtypesOptions,
    selectedIssueTypeData,
    setSubtypeFilterByText,
    isLoading: issueTypesQuery.isLoading || issueSubtypesQuery.isLoading,
    issueTypesQuery,
    issueSubtypesQuery,
  };
}
